// Quill 編輯器 plugin：全域註冊 QuillEditor 元件並載入一次主題樣式，
// 另 export 工具列 / 圖片模組設定供各處以 props 綁定。
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import axios from 'axios';
import ImageUploader from 'quill-image-uploader';
import { useToast } from '../composables/useToast';
import questionsAPI from '../apis/questions';

/**
 * Quill ToolBar
 */
export const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'], // toggled buttons
  ['link', 'code', 'code-block'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['image'],
  ['clean'], // remove formatting button
];

/**
 * 圖片格式判斷
 */
export const allowImageMime = (type) => {
  const allowImageType = {
    'image/png': 'png',
    'image/jpg': 'jpeg',
    'image/jpeg': 'jpeg',
  };
  return allowImageType[type];
};

/**
 * 封裝 FileReader
 */
export const readFileBuffer = (file) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.readAsArrayBuffer(file);
  fr.onload = (e) => {
    const { result } = e.target;
    resolve(result);
  };
  fr.onerror = reject;
});

/**
 * Quill 圖片模組
 */
export const modulesOptions = {
  name: 'ImageUploader',
  module: ImageUploader,
  options: {
    upload(file) {
      const { postMessage } = useToast();
      return new Promise((resolve, reject) => {
        const { type, size } = file;
        if (!allowImageMime(type)) {
          postMessage(
            'danger',
            '圖片格式錯誤',
            '僅限上傳 png、jpg 與 jpeg 檔案格式',
          );
          reject(new Error('僅限上傳 png、jpg 與 jpeg 檔案格式'));
          return;
        }

        const maxSize = 1024 * 1024 * 3;
        if (size >= maxSize) {
          postMessage('danger', '圖片過大', '圖片大小不得超過 3MB。');
          reject(new Error('圖片大小不得超過 3MB。'));
          return;
        }

        const uploadImg = async () => {
          try {
            const buffer = await readFileBuffer(file);

            const { data } = await questionsAPI.apiGetImageUploadUrl(allowImageMime(type));

            const cacheImgUrl = data.image_url;

            const imageUploadApiUrl = process.env.NODE_ENV === 'production'
              ? data.upload_url
              : `/images.hexschool.io/qa${data.upload_url.split('qa')[1]}`;
            // 直接對後端回傳的預簽章網址 PUT 上傳，屬外部儲存端點而非本站 API，
            // 不需 Bearer Token / XSRF，故刻意使用原生 axios（withCredentials: false 並移除 Authorization），
            // 不走 src/apis 的 Request 實例。
            const config = {
              transformRequest(reqData, headers) {
                // eslint-disable-next-line no-param-reassign
                delete headers.common.Authorization;
                return reqData;
              },
              headers: {
                'Content-Type': type,
              },
              withCredentials: false,
            };
            await axios.put(imageUploadApiUrl, buffer, config);
            resolve(cacheImgUrl);
          } catch (error) {
            postMessage('danger', '圖片上傳失敗', '圖片上傳失敗，請重新上傳。');
            reject(new Error('圖片上傳失敗。'));
          }
        };

        uploadImg();
      });
    },
  },
};

// Vue plugin：全域註冊 QuillEditor，元件即可直接使用 <QuillEditor>，
// 不必各自 import 元件與樣式。
export default {
  install(app) {
    app.component('QuillEditor', QuillEditor);
  },
};
